import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";

import { Footer } from "../../../components/Footer/Footer";
import { GetLabsAvailabilityApiService } from "../../../services/GetLabsAvailabilityApiService";
import { PhlebotomyApiService } from "../../../services/PhlebotomyApiService";
import {
  buildPhlebotomyFinderTrackingContext,
  trackPhlebotomySearchClick,
} from "../../../services/PhlebotomyTrackingService";
import {
  PHLEBOTOMY_MOBILE_REQUEST_URL,
  PHLEBOTOMY_MOBILE_SCHEDULE_URL,
  PHLEBOTOMY_SEARCH_URL,
} from "../../../utils/urlConstants";
import { FeatureFlags } from "../../../utils/launchDarklyUtils";
import PhlebotomyHeader from "../components/PhlebotomyHeader";
import PhlebotomyBackButton from "../components/PhlebotomyBackButton";
import { PageContainer } from "../components/PhlebotomySplitPane.styles";
import {
  MobilePhlebotomyLink,
  MobilePhlebotomyPhoneNumber,
  PhlebotomyLocation,
  formatPhoneForTel,
} from "../types";
import PhlebotomyLocationList from "../Finder/PhlebotomyLocationList";
import { LocationCardAction } from "../Finder/PhlebotomyLocationCard";
import PhlebotomyPhoneCard from "../PhoneOptions/PhlebotomyPhoneCard";
import MobilePhlebotomyWebsiteCard from "./MobilePhlebotomyWebsiteCard";
import MobilePhlebotomyGetLabsCard from "./MobilePhlebotomyGetLabsCard";
import MobilePhlebotomySupportButton from "./MobilePhlebotomySupportButton";
import { MobilePhlebotomyRequestNavigationState } from "./MobilePhlebotomyRequestPage";
import {
  AddressSupport,
  AddressTitle,
  FlowPageContent,
  MobileOptionsPageShell,
  MobileOptionsTextInset,
} from "./PhlebotomySearchPage.styles";
import { createAnonymousId, isUuid } from "./phlebotomyFormUtils";
import { usePhlebotomySalesforceFinderRollout } from "../../../utils/hooks/usePhlebotomySalesforceFinderRollout";

export type MobilePhlebotomyOptionsNavigationState =
  MobilePhlebotomyRequestNavigationState & {
    mobileLocations?: PhlebotomyLocation[];
  };

const CATALOG_FINDER_LEGACY_FALLBACK_TIMEOUT_MS = 5000;

const ignoreTrackingFailure = (promise: Promise<unknown>) => {
  promise.catch(() => undefined);
};

const MobilePhlebotomyOptionsPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const routerLocation = useLocation<MobilePhlebotomyOptionsNavigationState>();
  const flags = useFlags();
  const ldClient = useLDClient();
  const isGetLabsSandboxEnvironment =
    import.meta.env.VITE_ENV === "staging" ||
    import.meta.env.VITE_ENV === "local";
  const isGetLabsSandboxUiEnabled =
    isGetLabsSandboxEnvironment &&
    flags[FeatureFlags.PatientPhlebotomyGetLabsSandbox] === true;
  const isGetLabsSandboxFlagPending =
    isGetLabsSandboxEnvironment &&
    !Object.prototype.hasOwnProperty.call(
      flags,
      FeatureFlags.PatientPhlebotomyGetLabsSandbox,
    ) &&
    !ldClient;
  const {
    contextKey: catalogFinderContextKey,
    isEnabled: catalogFinderEnabled,
    isResolved: isCatalogFinderFlagResolved,
  } = usePhlebotomySalesforceFinderRollout();
  const query = useMemo(
    () => new URLSearchParams(routerLocation.search),
    [routerLocation.search],
  );
  const clinicPublicId = query.get("clinic")?.trim() || undefined;
  const zip = query.get("zip")?.trim() || undefined;
  const incomingTrackingId = useMemo(() => {
    const value = query.get("tracking_id")?.trim();
    return isUuid(value) ? value : undefined;
  }, [query]);
  const incomingUserId = useMemo(() => {
    const value = query.get("user_id")?.trim();
    return value && value.length <= 255 ? value : undefined;
  }, [query]);
  const [analyticsId] = useState(
    () =>
      routerLocation.state?.analyticsId ??
      incomingTrackingId ??
      incomingUserId ??
      createAnonymousId(),
  );
  const trackingContext = useMemo(
    () =>
      buildPhlebotomyFinderTrackingContext({
        distinctId: analyticsId,
        clinicPublicId,
        userId: incomingUserId,
        trackingId: incomingTrackingId,
        pageName: "MobilePhlebotomyOptionsPage",
      }),
    [analyticsId, clinicPublicId, incomingTrackingId, incomingUserId],
  );
  const [links, setLinks] = useState<MobilePhlebotomyLink[]>([]);
  const [phones, setPhones] = useState<MobilePhlebotomyPhoneNumber[]>([]);
  const hasNavigationMobileLocations =
    routerLocation.state?.mobileLocations !== undefined;
  const [catalogLocations, setCatalogLocations] = useState<
    PhlebotomyLocation[]
  >(() =>
    (routerLocation.state?.mobileLocations ?? []).filter(
      (location: PhlebotomyLocation) =>
        location.siteType === "mobile_phlebotomy",
    ),
  );
  const [selectedCatalogLocationIndex, setSelectedCatalogLocationIndex] =
    useState<number | null>(null);
  const [loading, setLoading] = useState(
    Boolean(clinicPublicId) || Boolean(zip && !hasNavigationMobileLocations),
  );
  const [error, setError] = useState<string | null>(null);
  const [hasCompletedOptionsLoad, setHasCompletedOptionsLoad] = useState(
    hasNavigationMobileLocations,
  );
  const [catalogFinderFallback, setCatalogFinderFallback] = useState(false);
  const [getLabsReady, setGetLabsReady] = useState<boolean | null>(null);
  const emptyOptionsRedirectStartedRef = useRef(false);
  const textPhones = phones.filter((phone) => phone.textToBook);
  const nonTextPhones = phones.filter((phone) => !phone.textToBook);
  const hasMobileOptions =
    (isGetLabsSandboxUiEnabled && getLabsReady === true) ||
    catalogLocations.length > 0 ||
    links.length > 0 ||
    phones.length > 0;
  const isCatalogFinderReady =
    isCatalogFinderFlagResolved || catalogFinderFallback;
  const isGetLabsReadinessPending =
    isGetLabsSandboxUiEnabled && getLabsReady === null;
  const isOptionsLoading = loading || isGetLabsReadinessPending;

  useEffect(() => {
    let isCurrent = true;
    if (!isGetLabsSandboxUiEnabled) {
      setGetLabsReady(false);
      return () => {
        isCurrent = false;
      };
    }

    setGetLabsReady(null);
    GetLabsAvailabilityApiService.getReadiness()
      .then(({ enabled }) => {
        if (isCurrent) setGetLabsReady(enabled);
      })
      .catch(() => {
        if (isCurrent) setGetLabsReady(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [isGetLabsSandboxUiEnabled]);

  useEffect(() => {
    if (isCatalogFinderFlagResolved) {
      setCatalogFinderFallback(false);
      return;
    }

    const fallbackTimer = window.setTimeout(
      () => setCatalogFinderFallback(true),
      CATALOG_FINDER_LEGACY_FALLBACK_TIMEOUT_MS,
    );
    return () => window.clearTimeout(fallbackTimer);
  }, [isCatalogFinderFlagResolved]);

  useEffect(() => {
    let isCurrent = true;

    const loadOptions = async () => {
      if (!clinicPublicId && hasNavigationMobileLocations) {
        setLinks([]);
        setPhones([]);
        setLoading(false);
        setError(null);
        setHasCompletedOptionsLoad(true);
        return;
      }

      if (!clinicPublicId && (!zip || !isCatalogFinderReady)) {
        if (!zip) setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        if (!clinicPublicId) {
          if (!catalogFinderEnabled) {
            setCatalogLocations([]);
            setHasCompletedOptionsLoad(true);
            return;
          }
          const data = await PhlebotomyApiService.searchPhlebotomyLocations(
            { zip: zip as string },
            {
              useSalesforceCatalog: true,
              finderSessionId: catalogFinderContextKey,
            },
          );
          if (!isCurrent) return;
          setCatalogLocations(
            data.locations.filter(
              (location) => location.siteType === "mobile_phlebotomy",
            ),
          );
          setHasCompletedOptionsLoad(true);
          return;
        }

        const data =
          await PhlebotomyApiService.getPhlebotomyServices(clinicPublicId);
        if (!isCurrent) return;
        setLinks(data.mobilePhlebotomyLinks);
        setPhones(data.mobilePhlebotomyPhoneNumbers);
        setHasCompletedOptionsLoad(true);
      } catch (err) {
        if (!isCurrent) return;
        setLinks([]);
        setPhones([]);
        setError(
          err instanceof Error
            ? err.message
            : t("phlebotomySearch.mobileOptions.loadError"),
        );
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadOptions();
    return () => {
      isCurrent = false;
    };
  }, [
    catalogFinderContextKey,
    catalogFinderEnabled,
    clinicPublicId,
    hasNavigationMobileLocations,
    isCatalogFinderReady,
    t,
    zip,
  ]);

  const returnToSearch = () => {
    const fallbackQuery = new URLSearchParams(routerLocation.search);
    if (clinicPublicId) fallbackQuery.set("clinic", clinicPublicId);
    if (zip) fallbackQuery.set("zip", zip);

    if (routerLocation.state?.returnTo) {
      history.push(
        routerLocation.state.returnTo,
        routerLocation.state.returnSearchState,
      );
      return;
    }

    history.push(
      `${PHLEBOTOMY_SEARCH_URL}${
        fallbackQuery.toString() ? `?${fallbackQuery}` : ""
      }`,
    );
  };

  const openWhiteGlove = useCallback(
    (returnDirectlyToFinder: boolean) => {
      if (trackingContext) {
        ignoreTrackingFailure(
          trackPhlebotomySearchClick(
            trackingContext,
            "mobile_phlebotomy_request_opened",
          ),
        );
      }

      const nextSearch = new URLSearchParams(routerLocation.search);
      const fallbackFinderUrl = `${PHLEBOTOMY_SEARCH_URL}${
        nextSearch.toString() ? `?${nextSearch}` : ""
      }`;
      const navigation = {
        pathname: PHLEBOTOMY_MOBILE_REQUEST_URL,
        search: nextSearch.toString() ? `?${nextSearch}` : "",
        state: {
          analyticsId,
          defaultFirstName: routerLocation.state?.defaultFirstName,
          defaultLastName: routerLocation.state?.defaultLastName,
          defaultPhoneNumber: routerLocation.state?.defaultPhoneNumber,
          defaultEstimatedDueDate:
            routerLocation.state?.defaultEstimatedDueDate,
          returnSearchState: returnDirectlyToFinder
            ? routerLocation.state?.returnSearchState
            : routerLocation.state,
          returnTo: returnDirectlyToFinder
            ? (routerLocation.state?.returnTo ?? fallbackFinderUrl)
            : `${routerLocation.pathname}${routerLocation.search}`,
          returnLocationKey: returnDirectlyToFinder
            ? undefined
            : routerLocation.key,
        } satisfies MobilePhlebotomyRequestNavigationState,
      };

      if (returnDirectlyToFinder) {
        history.replace(navigation);
      } else {
        history.push(navigation);
      }
    },
    [analyticsId, history, routerLocation, trackingContext],
  );

  const openGetLabsScheduler = () => {
    if (trackingContext) {
      ignoreTrackingFailure(
        trackPhlebotomySearchClick(
          trackingContext,
          "mobile_phlebotomy_schedule_opened",
        ),
      );
    }

    const nextSearch = new URLSearchParams(routerLocation.search);
    nextSearch.delete("fulfillment");
    nextSearch.delete("zip");
    history.push({
      pathname: PHLEBOTOMY_MOBILE_SCHEDULE_URL,
      search: nextSearch.toString() ? `?${nextSearch}` : "",
      state: {
        analyticsId,
        initialZip: zip,
        defaultFirstName: routerLocation.state?.defaultFirstName,
        defaultLastName: routerLocation.state?.defaultLastName,
        defaultPhoneNumber: routerLocation.state?.defaultPhoneNumber,
        defaultEstimatedDueDate: routerLocation.state?.defaultEstimatedDueDate,
        returnSearchState: routerLocation.state,
        returnTo: `${routerLocation.pathname}${routerLocation.search}`,
        returnLocationKey: routerLocation.key,
      } satisfies MobilePhlebotomyRequestNavigationState,
    });
  };

  useEffect(() => {
    if (
      emptyOptionsRedirectStartedRef.current ||
      loading ||
      error ||
      !hasCompletedOptionsLoad ||
      isGetLabsSandboxFlagPending ||
      isGetLabsReadinessPending ||
      hasMobileOptions
    ) {
      return;
    }

    emptyOptionsRedirectStartedRef.current = true;
    openWhiteGlove(true);
  }, [
    error,
    getLabsReady,
    hasCompletedOptionsLoad,
    hasMobileOptions,
    isGetLabsReadinessPending,
    isGetLabsSandboxUiEnabled,
    isGetLabsSandboxFlagPending,
    loading,
    openWhiteGlove,
  ]);

  const handleWebsiteSchedule = (link: MobilePhlebotomyLink) => {
    if (trackingContext) {
      ignoreTrackingFailure(
        trackPhlebotomySearchClick(
          trackingContext,
          "mobile_phlebotomy_website_opened",
        ),
      );
    }
    window.open(link.link, "_blank", "noopener,noreferrer");
  };

  const handlePhoneAction = (
    action: "call" | "text",
    phone: MobilePhlebotomyPhoneNumber,
  ) => {
    if (trackingContext) {
      ignoreTrackingFailure(
        trackPhlebotomySearchClick(
          trackingContext,
          action === "call"
            ? "mobile_phlebotomy_phone_call"
            : "mobile_phlebotomy_phone_text",
        ),
      );
    }

    if (action === "call") {
      window.location.href = formatPhoneForTel(phone);
      return;
    }

    window.location.href = `sms:${phone.number}?body=${encodeURIComponent(
      t("phlebotomy.mobilePhlebotomy.smsMessage"),
    )}`;
  };

  const handleCatalogLocationAction = (
    action: LocationCardAction,
    location: PhlebotomyLocation,
  ) => {
    if (!trackingContext) return;
    ignoreTrackingFailure(
      trackPhlebotomySearchClick(trackingContext, action, {
        location_name: location.name,
      }),
    );
  };

  return (
    <PageContainer>
      <PhlebotomyHeader />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <MobileOptionsPageShell>
          <FlowPageContent>
            <MobileOptionsTextInset>
              <PhlebotomyBackButton onClick={returnToSearch} />
              <AddressTitle component="h2" sx={{ mt: 1.5 }}>
                {t("phlebotomySearch.mobileOptions.title")}
              </AddressTitle>
              <AddressSupport sx={{ mb: 0.5 }}>
                {t("phlebotomySearch.mobileOptions.support")}
              </AddressSupport>
            </MobileOptionsTextInset>

            {isOptionsLoading && (
              <Box
                role="status"
                aria-label={t("phlebotomySearch.mobileOptions.loading")}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            {!isOptionsLoading && error && (
              <MobileOptionsTextInset>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  role="status"
                  sx={{ mb: 2 }}
                >
                  {t(
                    clinicPublicId
                      ? "phlebotomySearch.mobileOptions.loadError"
                      : "phlebotomySearch.mobileOptions.catalogLoadError",
                  )}
                </Typography>
              </MobileOptionsTextInset>
            )}

            {!isOptionsLoading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {isGetLabsSandboxUiEnabled && getLabsReady === true && (
                  <MobilePhlebotomyGetLabsCard
                    onSchedule={openGetLabsScheduler}
                  />
                )}
                {catalogLocations.length > 0 && (
                  <PhlebotomyLocationList
                    locations={catalogLocations}
                    selectedLocationIndex={selectedCatalogLocationIndex}
                    onLocationSelect={setSelectedCatalogLocationIndex}
                    onButtonClick={handleCatalogLocationAction}
                  />
                )}
                {textPhones.map((phone) => (
                  <PhlebotomyPhoneCard
                    key={`${phone.name}-${phone.number}`}
                    phone={phone}
                    onButtonClick={handlePhoneAction}
                  />
                ))}
                {links.map((link) => (
                  <MobilePhlebotomyWebsiteCard
                    key={`${link.name}-${link.link}`}
                    link={link}
                    onSchedule={handleWebsiteSchedule}
                  />
                ))}
                {nonTextPhones.map((phone) => (
                  <PhlebotomyPhoneCard
                    key={`${phone.name}-${phone.number}`}
                    phone={phone}
                    onButtonClick={handlePhoneAction}
                  />
                ))}
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <MobilePhlebotomySupportButton
                    onRequest={() => openWhiteGlove(false)}
                  />
                </Box>
              </Box>
            )}
          </FlowPageContent>
        </MobileOptionsPageShell>
      </Box>
      <Footer showLanguageSwitcher requireLanguageFeatureFlag={false} />
    </PageContainer>
  );
};

export default MobilePhlebotomyOptionsPage;
